import { TwButton as Button, Icon, TwSelector as Selector, Text } from '@ui-component-library';

import { DynamicModal } from '@components/DynamicModal';
import { SkeletonLoader } from '@components/PropertySelector/components/SkeletonLoaders';

import { SelectProductDialogProps } from '../../Checkout.types';
import { useCheckout } from '../../context/CheckoutContext';

export function SelectProductDialog({
  selectedProducts,
  onToggleProduct,
  confirmLabel = 'Klar',
  emptyContent = null,
  loading = false,
  onPrimaryAction = () => {},
  primaryRequiresNonEmptySelection = false
}: SelectProductDialogProps) {
  const { openSelectProductDialog, setOpenSelectProductDialog, userProducts, config } =
    useCheckout();

  const options = userProducts;

  const handleClose = () => {
    setOpenSelectProductDialog(false);
  };
  const isLoading = Boolean(config.loading) || Boolean(loading);
  const selectedProductIds = selectedProducts.map((p) => p.id);
  const selectedIdSet = new Set(selectedProductIds);
  const isEmpty = !options.length;
  const selectedCount = selectedProducts.length;

  const footerDisabled = isLoading || (primaryRequiresNonEmptySelection && selectedCount === 0);
  const breakpoints = { sm: 'sheet', lg: 'sheet' };

  return (
    <DynamicModal bp={breakpoints} isOpen={openSelectProductDialog} onClose={handleClose}>
      <DynamicModal.Header>
        <div className="flex w-full items-center justify-between">
          <Text variant="text-lg/leading-normal/medium">{config.selectDialogTitle}</Text>
          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
          </Button>
        </div>
      </DynamicModal.Header>
      <DynamicModal.Content className="p-0">
        {config.searchField ?? null}
        <div className="flex flex-col gap-2 p-4">
          {isLoading && (
            <div className="mt-6 flex flex-col gap-3">
              <SkeletonLoader length={10} type="searchResult" />
            </div>
          )}
          {!isEmpty && (
            <Selector>
              {options.map((option) => (
                <div key={option.id} className="flex flex-row items-center justify-between gap-1">
                  <Selector.Option
                    label={option.name.toLowerCase()}
                    value={option.id}
                    showDefaultIcon
                    isSelected={selectedIdSet.has(option.id)}
                    onClick={() => onToggleProduct(option)}
                    disabled={config.isOptionDisabled?.(option) ?? false}
                    className="gap-1 capitalize"
                  >
                    {config.renderOptionBadge?.(option)}
                  </Selector.Option>
                </div>
              ))}
            </Selector>
          )}

          {isEmpty && emptyContent !== null ? emptyContent : null}
        </div>
      </DynamicModal.Content>
      <DynamicModal.Footer className="gap-2 p-0">
        <div className="p-4">
          <Button
            variant="primary"
            fullWidth
            disabled={footerDisabled}
            onClick={() => {
              void onPrimaryAction();
            }}
          >
            {isLoading ? 'Laddar...' : confirmLabel}
            {selectedCount > 0 && !isLoading && (
              <Text
                variant="text-xs/leading-normal/semibold"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-[#F5F5F5] text-[#1A1A1A]"
              >
                <span>{selectedCount}</span>
              </Text>
            )}
          </Button>
        </div>
      </DynamicModal.Footer>
    </DynamicModal>
  );
}
